import { strapi } from './strapi-client';
import { isUserAdminForCompany } from '../redux/locations';

export async function getLocations(company) {
  // Input validation
  if (!company || !company.id) {
    console.error('getLocations: Invalid company parameter provided:', company);
    return [];
  }

  // Validate company ID
  const companyId = parseInt(company.id);
  if (isNaN(companyId) || companyId <= 0) {
    console.error(
      'getLocations: Invalid company ID format or value:',
      company.id
    );
    return [];
  }

  try {
    const data = await strapi.find('locations', {
      pagination: { page: 1, pageSize: 10000 },
      filters: {
        company: {
          id: {
            $eq: companyId,
          },
        },
      },
      populate: {
        address: true,
      },
      sort: { id: 'asc' },
    });

    return data;
  } catch (error) {
    console.error(
      'getLocations: Error fetching locations for company',
      companyId,
      ':',
      error
    );
    return [];
  }
}

export async function getLocationsCount(company) {
  const { meta } = await strapi.find('locations', {
    filters: {
      company: { id: { $eq: parseInt(company.id) } },
    },
    fields: ['id'],
    pagination: { pageSize: 1 },
  });
  return meta.pagination.total;
}

/**
 * Get locations filtered by user permissions
 * If user is admin, return all locations
 * Otherwise, return only locations where user is assigned as employee
 */
export async function getUserFilteredLocations(company, user) {
  // Check if user is admin for this company using centralized utility
  const isAdmin = isUserAdminForCompany(user, company);

  let filters = {
    company: {
      id: {
        $eq: parseInt(company.id),
      },
    },
  };

  // If user is not admin, filter by locations where user is assigned as employee
  if (!isAdmin && user?.locations && user.locations.length > 0) {
    filters.id = {
      $in: user.locations.map((location) => location.id),
    };
  } else if (!isAdmin) {
    // If user is not admin and has no assigned locations, return empty result
    filters.id = {
      $in: [-1], // Non-existent ID to return empty results
    };
  }

  const data = await strapi.find('locations', {
    pagination: { page: 1, pageSize: 10000 },
    filters,
    populate: {
      address: true,
    },
    sort: { id: 'asc' },
  });

  return data;
}

// create/update/delete THROW rather than return null: they back useMutation in
// settings/company/Locations.js, and a resolved null fired onSuccess — a
// "Location created successfully!" toast for a location that was never sent.
export async function createLocation(location) {
  // Input validation
  if (!location || typeof location !== 'object') {
    throw new Error('createLocation: Invalid location parameter provided');
  }

  // Validate required fields
  if (!location.name || typeof location.name !== 'string') {
    throw new Error(
      'createLocation: Location name is required and must be a string'
    );
  }

  // The settings form sends the bare id (`company: company.id`); accept a
  // populated object too.
  const companyId =
    location.company && typeof location.company === 'object'
      ? location.company.id
      : location.company;
  if (!companyId) {
    throw new Error(
      'createLocation: Location must have a valid company reference'
    );
  }

  try {
    const { data } = await strapi.create(
      'locations',
      { ...location, company: companyId },
      {
        populate: {
          address: true,
        },
      }
    );
    return data;
  } catch (error) {
    console.error('createLocation: Error creating location:', error);
    throw error;
  }
}

export async function updateLocation(location) {
  // Input validation
  if (!location || typeof location !== 'object') {
    throw new Error('updateLocation: Invalid location parameter provided');
  }

  // Validate location ID
  if (
    !location.id ||
    (typeof location.id !== 'string' && typeof location.id !== 'number')
  ) {
    throw new Error('updateLocation: Invalid location ID provided');
  }

  const locationId =
    typeof location.id === 'string' ? parseInt(location.id, 10) : location.id;
  if (isNaN(locationId) || locationId <= 0) {
    throw new Error('updateLocation: Invalid location ID format or value');
  }

  try {
    const { data } = await strapi.update('locations', locationId, location, {
      populate: {
        address: true,
      },
    });
    return data;
  } catch (error) {
    console.error(
      'updateLocation: Error updating location with id',
      locationId,
      ':',
      error
    );
    throw error;
  }
}

export async function deleteLocation(id) {
  // Input validation
  if (!id || (typeof id !== 'string' && typeof id !== 'number')) {
    throw new Error('deleteLocation: Invalid id parameter provided');
  }

  // Convert string to number if needed and validate
  const locationId = typeof id === 'string' ? parseInt(id, 10) : id;
  if (isNaN(locationId) || locationId <= 0) {
    throw new Error('deleteLocation: Invalid id format or value');
  }

  try {
    const { data } = await strapi.delete('locations', locationId);
    return data;
  } catch (error) {
    console.error(
      'deleteLocation: Error deleting location with id',
      locationId,
      ':',
      error
    );
    throw error;
  }
}

export async function getLocation(id) {
  // Input validation
  if (!id || (typeof id !== 'string' && typeof id !== 'number')) {
    console.error('getLocation: Invalid id parameter provided:', id);
    return null;
  }

  // Convert string to number if needed and validate
  const locationId = typeof id === 'string' ? parseInt(id, 10) : id;
  if (isNaN(locationId) || locationId <= 0) {
    console.error('getLocation: Invalid id format or value:', id);
    return null;
  }

  try {
    const { data } = await strapi.findOne('locations', locationId, {
      populate: {
        address: true,
      },
    });
    return data;
  } catch (error) {
    console.error(
      'getLocation: Error fetching location with id',
      locationId,
      ':',
      error
    );
    return null;
  }
}
